import React from "react";
import { create } from "zustand";
import { toast } from "sonner";
import ToastCompact from "@/components/common/toast/ToastCompact";

export interface AttachedCreative {
  creative_id: string;
  ad_id: string;
  section_id: string;
  thumbnail_url?: string;
  media_url: string;
  type: "image" | "video" | "mixed";
  ad_name?: string;
}

interface AttachedCreativesState {
  attachedCreatives: AttachedCreative[];
  currentSectionId: string | null;
  currentSectionMetrics: { id: string; label: string }[];
  currentSectionGroupBy: string | null;
  isChatOpen: boolean;
  shouldPinOnOpen: boolean;
  chatOpenedAt: number | null;
  addAttachedCreative: (creative: AttachedCreative) => void;
  removeAttachedCreative: (creative_id: string, section_id: string) => void;
  clearAttachedCreatives: () => void;
  isCreativeAttached: (creative_id: string, section_id: string) => boolean;
  setIsChatOpen: (open: boolean) => void;
  setShouldPinOnOpen: (shouldPin: boolean) => void;
  setCurrentSectionMetrics: (
    sectionId: string,
    metrics: { id: string; label: string }[],
  ) => void;
  setCurrentSectionGroupBy: (sectionId: string, groupBy: string | null) => void;
  // Helper to attach creative and open chat
  attachCreativeAndOpenChat: (
    creative: AttachedCreative,
    groupBy?: string | null,
    metrics?: { id: string; label: string }[],
  ) => void;
  // Get current section id
  getCurrentSectionId: () => string | null;
  // Check if within 5 seconds of chat opening and close if so
  detachAndMaybeCloseChat: (creative_id: string, section_id: string) => void;
}

export const useAttachedCreativesStore = create<AttachedCreativesState>((set, get) => ({
  attachedCreatives: [],
  currentSectionId: null,
  currentSectionMetrics: [],
  currentSectionGroupBy: null,
  isChatOpen: false,
  shouldPinOnOpen: false,
  chatOpenedAt: null,

  addAttachedCreative: (creative) =>
    set((state) => {
      // Prevent duplicates (check both creative_id and section_id)
      if (
        state.attachedCreatives.some(
          (c) =>
            c.creative_id === creative.creative_id &&
            c.section_id === creative.section_id,
        )
      ) {
        return state;
      }
      // Check if limit of 5 creatives is reached
      const willExceedLimit =
        state.currentSectionId === creative.section_id &&
        state.attachedCreatives.length >= 5;
      if (willExceedLimit) {
        toast.custom(() =>
          React.createElement(ToastCompact, {
            messagePrefix: "You can add 5 creatives max",
            descriptionPrefix: "Free up a slot to add one.",
            isWarning: true,
            hasDescription: true,
          }),
        );
        return state;
      }
      // If creative is from a different section, clear existing creatives
      if (state.currentSectionId && state.currentSectionId !== creative.section_id) {
        return {
          attachedCreatives: [creative],
          currentSectionId: creative.section_id,
        };
      }
      return {
        attachedCreatives: [...state.attachedCreatives, creative],
        currentSectionId: creative.section_id,
      };
    }),

  removeAttachedCreative: (creative_id, section_id) =>
    set((state) => {
      const newCreatives = state.attachedCreatives.filter(
        (c) => !(c.creative_id === creative_id && c.section_id === section_id),
      );
      return {
        attachedCreatives: newCreatives,
        // Clear section id if no more creatives
        currentSectionId: newCreatives.length > 0 ? state.currentSectionId : null,
      };
    }),

  clearAttachedCreatives: () =>
    set({
      attachedCreatives: [],
      currentSectionId: null,
      currentSectionMetrics: [],
      currentSectionGroupBy: null,
    }),

  isCreativeAttached: (creative_id, section_id) => {
    return get().attachedCreatives.some(
      (c) => c.creative_id === creative_id && c.section_id === section_id,
    );
  },

  setIsChatOpen: (open) => set({ isChatOpen: open }),

  setShouldPinOnOpen: (shouldPin) => set({ shouldPinOnOpen: shouldPin }),

  setCurrentSectionMetrics: (sectionId, metrics) => {
    const state = get();
    // Only update if this is the current section with attached creatives
    if (state.currentSectionId === sectionId) {
      set({ currentSectionMetrics: metrics });
    }
  },

  setCurrentSectionGroupBy: (sectionId, groupBy) => {
    const state = get();
    // Only process if this is the current section with attached creatives
    if (state.currentSectionId === sectionId) {
      const previousGroupBy = state.currentSectionGroupBy;
      // If groupBy changes from "creative_name" to something else, detach all creatives
      if (previousGroupBy === "creative_name" && groupBy !== "creative_name") {
        set({
          attachedCreatives: [],
          currentSectionId: null,
          currentSectionMetrics: [],
          currentSectionGroupBy: groupBy,
          isChatOpen: false,
          shouldPinOnOpen: false,
          chatOpenedAt: null,
        });
      } else {
        // Just update the groupBy value
        set({ currentSectionGroupBy: groupBy });
      }
    }
  },

  attachCreativeAndOpenChat: (
    creative,
    groupBy?: string | null,
    metrics?: { id: string; label: string }[],
  ) => {
    const state = get();
    // Only allow attachment if groupBy is "creative_name"
    if (groupBy !== "creative_name") {
      return;
    }
    const now = Date.now();
    // Only pin if chat is not already open
    // Check session storage to see if user manually unpinned
    const manuallyUnpinned =
      sessionStorage.getItem("copilot_manually_unpinned") === "true";
    const shouldPin = !state.isChatOpen && !manuallyUnpinned;
    // If creative is already attached (same creative_id AND section_id), just open chat
    if (
      state.attachedCreatives.some(
        (c) =>
          c.creative_id === creative.creative_id && c.section_id === creative.section_id,
      )
    ) {
      set({
        isChatOpen: true,
        ...(shouldPin ? { shouldPinOnOpen: true, chatOpenedAt: now } : {}),
        ...(metrics ? { currentSectionMetrics: metrics } : {}),
      });
      return;
    }
    // Check if limit of 5 creatives is reached (only for same section)
    const willExceedLimit =
      (!state.currentSectionId || state.currentSectionId === creative.section_id) &&
      state.attachedCreatives.length >= 5;
    if (willExceedLimit) {
      toast.custom(() =>
        React.createElement(ToastCompact, {
          messagePrefix: "You can add 5 creatives max",
          descriptionPrefix: "Free up a slot to add one.",
          isWarning: true,
          hasDescription: true,
        }),
      );
      return;
    }
    // If creative is from a different section, clear existing and add new
    if (state.currentSectionId && state.currentSectionId !== creative.section_id) {
      set({
        attachedCreatives: [creative],
        currentSectionId: creative.section_id,
        currentSectionGroupBy: groupBy || null,
        currentSectionMetrics: metrics || [],
        isChatOpen: true,
        ...(shouldPin ? { shouldPinOnOpen: true, chatOpenedAt: now } : {}),
      });
    } else {
      // Same section or no section yet, add to existing
      set((s) => ({
        attachedCreatives: [...s.attachedCreatives, creative],
        currentSectionId: creative.section_id,
        currentSectionGroupBy: groupBy || null,
        currentSectionMetrics: metrics || s.currentSectionMetrics,
        isChatOpen: true,
        ...(shouldPin ? { shouldPinOnOpen: true, chatOpenedAt: now } : {}),
      }));
    }
  },

  getCurrentSectionId: () => get().currentSectionId,

  detachAndMaybeCloseChat: (creative_id, section_id) => {
    const state = get();
    const newCreatives = state.attachedCreatives.filter(
      (c) => !(c.creative_id === creative_id && c.section_id === section_id),
    );

    // Check if we should close the chat (within 5 seconds of opening AND only one creative was attached)
    const shouldCloseChat =
      state.attachedCreatives.length === 1 &&
      state.chatOpenedAt !== null &&
      Date.now() - state.chatOpenedAt < 5000;

    set({
      attachedCreatives: newCreatives,
      // Clear section id if no more creatives
      currentSectionId: newCreatives.length > 0 ? state.currentSectionId : null,
      // Close chat if detached within 5 seconds and it was the only creative
      ...(shouldCloseChat
        ? { isChatOpen: false, shouldPinOnOpen: false, chatOpenedAt: null }
        : {}),
    });
  },
}));
